'use client';

import { useEffect, useState } from 'react';

/**
 * Keep the screen awake while the operator holds an aircraft.
 *
 * A tablet display timeout is typically 30–60 seconds. An operator flying a
 * steady vector touches nothing for far longer than that, and a screen that
 * sleeps mid-vector takes the STOP key with it. The Screen Wake Lock API is
 * supported everywhere current, Safari 16.4+ included — but it is a REQUEST,
 * not a guarantee: a low battery, a power-saving mode or a page that is not
 * visible all let the system refuse, and the promise rejects.
 *
 * So it is wrapped, and a refusal is SAID rather than swallowed. `refused`
 * drives a persistent `SCREEN MAY SLEEP` note in the manual banner, which is
 * the honest outcome: the operator can set the display timeout by hand, but
 * only if they know the lock did not take.
 *
 * The lock is released automatically by the browser whenever the page stops
 * being visible, and is NOT re-acquired on its own — hence the
 * `visibilitychange` listener. That is the same event the manual controller
 * uses to send a hover, and the two are complementary: the aircraft stops when
 * the operator looks away, and the screen is re-held when they look back.
 */

interface WakeLockSentinelLike {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: 'release', listener: () => void) => void;
  removeEventListener: (type: 'release', listener: () => void) => void;
}

interface WakeLockNavigator {
  wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> };
}

export interface ScreenWakeLock {
  /** True while the system is actually holding the screen awake for us. */
  held: boolean;
  /**
   * True when a lock was wanted and could not be had — the API is absent, or
   * the system refused. Never true while nothing is being asked for.
   */
  refused: boolean;
}

export function useScreenWakeLock(active: boolean): ScreenWakeLock {
  const [held, setHeld] = useState(false);
  const [refused, setRefused] = useState(false);

  useEffect(() => {
    if (!active) {
      setHeld(false);
      setRefused(false);
      return;
    }

    const nav = navigator as Navigator & WakeLockNavigator;
    const api = nav.wakeLock;
    if (!api || typeof api.request !== 'function') {
      // Absent is a refusal in every way the operator cares about: the screen
      // may sleep. Saying "unsupported" instead would be a distinction without
      // a difference on a tablet in the field.
      setHeld(false);
      setRefused(true);
      return;
    }

    let cancelled = false;
    let sentinel: WakeLockSentinelLike | null = null;

    // The system drops the lock on its own (the page hid, the device dimmed by
    // policy). That is not our release, and the operator must see the screen is
    // no longer being held rather than a stale "held".
    //
    // A loss WHILE THE PAGE IS STILL VISIBLE is also a refusal in every way the
    // operator cares about: the screen may now sleep mid-vector, taking STOP
    // with it, and nothing else says so. `refused` used to cover only the
    // initial request, so a mid-session loss — a battery saver kicking in is the
    // normal way this happens, an hour into a session — was silent. A release
    // caused by the page being hidden is excluded because that one is the
    // documented hide-and-reacquire cycle, and `acquire` clears `refused` again
    // the moment the lock comes back.
    const onReleased = () => {
      if (cancelled) return;
      setHeld(false);
      if (document.visibilityState === 'visible') setRefused(true);
    };

    const acquire = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      // Already holding one: a second request would return a second sentinel and
      // leak the first.
      if (sentinel !== null && !sentinel.released) return;
      try {
        const next = await api.request('screen');
        if (cancelled) {
          void next.release().catch(() => undefined);
          return;
        }
        sentinel = next;
        next.addEventListener('release', onReleased);
        setHeld(true);
        setRefused(false);
      } catch {
        // NotAllowedError is the documented refusal, but any rejection means the
        // same thing here and none of them may throw out of an effect.
        if (cancelled) return;
        setHeld(false);
        setRefused(true);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      const current = sentinel;
      sentinel = null;
      if (current) {
        current.removeEventListener('release', onReleased);
        // Authority ended, so the screen goes back to the operator's own
        // timeout. Holding it past the session would flatten the tablet.
        void current.release().catch(() => undefined);
      }
    };
  }, [active]);

  return { held, refused };
}
